import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Typography
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DownloadIcon from '@mui/icons-material/Download';
import imgUrl from '../assets/images/imgUrl.js';
import { Link as ScrollLink } from 'react-scroll';
import { useNavigate, useLocation } from 'react-router-dom';

const navItems = [
  { label: 'Home', type: 'scroll', target: 'home' },
  { label: 'About', type: 'scroll', target: 'aboutGlobalReach' },
  { label: 'Products', type: 'scroll', target: 'products' },
  { label: 'Career', type: 'scroll', target: 'career' },
  { label: 'Contact us', type: 'scroll', target: 'contact-us' },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openResources, setOpenResources] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we're on the brochure page
  const isBrochurePage = location.pathname === '/brochure';

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const handleOpenMenu = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleDownload = useCallback((fileType) => {
    handleCloseMenu();

    if (fileType === "full") {
      navigate('/brochure');
    } 
    // Brief will be added later
  }, [navigate]);

  const handleNavClick = useCallback((target) => {
    // If we're not on the home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: target } });
    } else {
      // If we're already on home page, scroll directly
      const element = document.getElementById(target);
      if (element) {
        const offset = 70; // Same as ScrollLink offset
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  }, [location.pathname, navigate]);

  // Optimize scroll handler with requestAnimationFrame to prevent performance issues
  const scrollFrameRef = useRef(null);
  useEffect(() => {
    const handleScroll = () => {
      if (scrollFrameRef.current === null) {
        scrollFrameRef.current = requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 10);
          scrollFrameRef.current = null;
        });
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollFrameRef.current !== null) {
        cancelAnimationFrame(scrollFrameRef.current);
      }
    };
  }, []);

  // Memoize drawer to prevent unnecessary re-renders
  const drawer = useMemo(() => (
    <Box onClick={(e) => e.stopPropagation()} sx={{ textAlign: 'center' }}>
      <img
        src={imgUrl.dureLogo}
        alt="Logo"
        style={{ 
          height: 30, 
          margin: '16px auto', 
          cursor: 'pointer',
          filter: isBrochurePage ? 'invert(1)' : 'none'
        }}
        onClick={() => {
          handleDrawerToggle();
          navigate('/');
        }}
      />

      <List>
        {navItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            {location.pathname === '/' ? (
              <ScrollLink
                to={item.target}
                smooth={true}
                duration={500}
                offset={-70}
                spy={true}
                activeClass="activeScroll"
              >
                <ListItemButton sx={{ textAlign: 'center' }}>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ScrollLink>
            ) : (
              <ListItemButton 
                sx={{ textAlign: 'center' }}
                onClick={() => {
                  handleNavClick(item.target);
                  handleDrawerToggle();
                }}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            )}
          </ListItem>
        ))}

        {/* Resources Main Item */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setOpenResources(!openResources)}
            sx={{
              textAlign: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 500,
            }}
          >
            <ListItemText primary="Resources" />
          </ListItemButton>
        </ListItem>

        {/* Submenu Items */}
        {openResources && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 , paddingLeft:'10px' , paddingRight:'10px'}} className="resourceTab">
            <Button
              fullWidth
              variant="contained"
              className='resourcesButton'
              onClick={() => {
                handleDownload('full');
                handleDrawerToggle();
              }}
              sx={{
                backgroundColor: '#6e1b9b',
                color: '#fff',
                borderRadius: 10,
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
                '&:hover': { backgroundColor: '#4a148c' },
              }}
            >
              Full Brochure
            </Button>

            <Button
              fullWidth
              variant="contained"
              className='resourcesButton'
              onClick={() => handleDownload('brief')}
              sx={{
                backgroundColor: '#6e1b9b',
                color: '#fff',
                borderRadius: 10,
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
                '&:hover': { backgroundColor: '#4a148c' },
              }}
              startIcon={<DownloadIcon />}
              href=""
              download
            >
              Brief
            </Button>
          </Box>
        )}
      </List>
    </Box>
  ), [isBrochurePage, location.pathname, openResources, handleDrawerToggle, navigate, handleNavClick, handleDownload]);

  return (
    <>
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        className={`HeaderSection ${isScrolled ? 'scrolled' : ''}`}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={imgUrl.dureLogo}
              alt="Logo"
              className='dureLogo'
              style={{ 
                height: 30, 
                marginRight: 12, 
                cursor: 'pointer',
                filter: isBrochurePage ? 'invert(1)' : 'none'
              }}
              onClick={() => navigate('/')}
            />
          </Box>

          {isMobile ? (
            <IconButton color="inherit" edge="start" onClick={handleDrawerToggle}>
              <MenuIcon className="menuIcon" />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {navItems.map((item, index) => (
                location.pathname === '/' ? (
                  <ScrollLink
                    key={index}
                    to={item.target}
                    smooth={true}
                    duration={500}
                    offset={-70}
                    spy={true}
                    activeClass="activeScroll"
                  >
                    <Button sx={{ color: isBrochurePage ? '#000' : '#fff', textTransform: 'none' }}>
                      {item.label}
                    </Button>
                  </ScrollLink>
                ) : (
                  <Button 
                    key={index}
                    sx={{ color: isBrochurePage ? '#000' : '#fff', textTransform: 'none' }}
                    onClick={() => handleNavClick(item.target)}
                  >
                    {item.label}
                  </Button>
                )
              ))}

              {/* Resources Dropdown */}
              <Button
                variant="contained"
                color="secondary"
                onClick={handleOpenMenu}
                sx={{
                  ml: 2,
                  borderRadius: 10,
                  px: 3,
                  fontSize: '14px !important',
                  boxShadow: 'none',
                  backgroundColor: '#c170ff',
                  '&:hover': { backgroundColor: '#c170ff' },
                }}
              >
                Resources
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: 3,
                    boxShadow: '0px 4px 20px rgba(0,0,0,0.15)',
                    px: 1,
                    backgroundColor:'#f2f2f2'
                  },
                }}
              >
                <MenuItem onClick={() => handleDownload("full")}>
                  <Typography sx={{ flexGrow: 1 , color:'#8d8d8d'}}>Full Brochure</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleDownload("brief")}>
                  <Typography sx={{ flexGrow: 1  , color:'#8d8d8d'}}>Brief</Typography>
                  {/* <DownloadIcon fontSize="small" /> */}
                  <img src={imgUrl.downloadIcon} alt='Download' style={{marginLeft:'10px'}}></img>
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        className="mobileDrawer"
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: 240,
            paddingTop: 2,
            backgroundColor: 'rgb(51 71 125 / 80%)',
            color: '#fff',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
